import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const FrozenStepManager = ({
                               currentStepId,
                               contentRenderer,
                               shouldFreezeStep = () => false,
                               shouldUnfreezeStep = () => false
                           }) => {
    // Store IDs of steps to keep alive (mounted)
    const [frozenStepIds, setFrozenStepIds] = useState(() => new Set());

    const renderStepContent = (stepId) => {
        try {
            // Even though we call this function again, if it returns
            // the same Component type (e.g. <ContentArea />) with the same key,
            // React will preserve the internal state (inputs, scroll).
            const node = contentRenderer(stepId);
            return node;
        } catch (err) {
            console.error(`Error rendering step ${stepId}:`, err);
            return null;
        }
    };

    useEffect(() => {
        if (currentStepId == null) return;

        const freeze = typeof shouldFreezeStep === 'function' ? shouldFreezeStep(currentStepId) : false;
        const unfreeze = typeof shouldUnfreezeStep === 'function' ? shouldUnfreezeStep(currentStepId) : false;

        setFrozenStepIds((prevIds) => {
            const newIds = new Set(prevIds);
            if (unfreeze) newIds.delete(currentStepId);
            else if (freeze) newIds.add(currentStepId);

            return newIds.size !== prevIds.size ? newIds : prevIds;
        });
    }, [currentStepId, shouldFreezeStep, shouldUnfreezeStep]);

    if (currentStepId == null) return null;

    // We render the Current ID + All Frozen IDs
    const stepsToRender = Array.from(new Set([...frozenStepIds, currentStepId]));

    return (
        <div style={{ position: 'relative' }}>
            {stepsToRender.map((stepId) => {
                const isCurrent = stepId === currentStepId;

                return (
                    <div
                        // CRITICAL: This unique 'key' tells React:
                        // "This is the same component as last time. Do not destroy it."
                        key={`step-container-${stepId}`}
                        style={{
                            // CRITICAL: We use CSS to hide it.
                            // The DOM nodes (and user inputs) remain in memory.
                            display: isCurrent ? 'block' : 'none',
                            position: isCurrent ? 'relative' : 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                        }}
                    >
                        {renderStepContent(stepId)}
                    </div>
                );
            })}
        </div>
    );
};

FrozenStepManager.propTypes = {
    currentStepId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contentRenderer: PropTypes.func.isRequired,
    shouldFreezeStep: PropTypes.func,
    shouldUnfreezeStep: PropTypes.func
};

export default FrozenStepManager;